'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import RatingHistogram from './RatingHistogram';
import WriteReviewModal, { type ExistingReview } from './WriteReviewModal';

type Aggregate = { count: number; average: number; histogram: Record<string, number> };

type Review = {
  id: number;
  rating: number;
  body: string;
  createdAt: string;
  updatedAt: string | null;
  reviewer: { personId: number; stageName: string | null } | null;
};

type Eligibility = {
  eligible: boolean;
  source?: string;
  reason?: string;
  existingReviewId?: number;
};

function fmt(iso: string) {
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

export default function ReviewsTab({
  subjectPersonId,
  subjectName,
  initialAggregate,
  initialReviews,
}: {
  subjectPersonId: number;
  subjectName: string;
  initialAggregate: Aggregate;
  initialReviews: Review[];
}) {
  const [aggregate, setAggregate] = useState<Aggregate>(initialAggregate);
  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const [elig, setElig] = useState<Eligibility | null>(null);
  const [existing, setExisting] = useState<ExistingReview | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Eligibility check fires client-side because it depends on the JWT
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(
          `/api/proxy/api/reviews/eligibility?subjectPersonId=${subjectPersonId}`,
          undefined,
          { method: 'GET' },
        );
        if (!res.ok) return;
        const j = (await res.json()) as Eligibility;
        if (!cancelled) setElig(j);
        if (j?.eligible && j?.existingReviewId) {
          const own = reviews.find((r) => r.id === j.existingReviewId);
          if (own) setExisting({ id: own.id, rating: own.rating, body: own.body });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [subjectPersonId, reviews]);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/proxy/api/profile/${subjectPersonId}/reviews?limit=20`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const j = await res.json();
      setAggregate(j.aggregate);
      setReviews(j.reviews ?? []);
    } catch { /* silent */ }
  }, [subjectPersonId]);

  const buttonLabel = existing ? 'Edit your review' : 'Write a review';

  return (
    <section className="mt-8 rounded-lg border bg-card p-4 sm:p-6">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold">Reviews</h2>
        {elig?.eligible ? (
          <Button onClick={() => setModalOpen(true)}>{buttonLabel}</Button>
        ) : null}
      </div>

      {/* One empty state, not two: RatingHistogram renders its own
          "No reviews yet." at count 0, and the list used to add a second. */}
      {aggregate.count > 0 && <RatingHistogram aggregate={aggregate} />}

      {reviews.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No reviews yet. Members who have worked with {subjectName} can be the first.
        </div>
      ) : (
        <ul className="mt-6 space-y-4">
          {reviews.map((r) => {
            const name = r.reviewer?.stageName || 'A creator';
            const href = r.reviewer?.stageName ? `/u/${encodeURIComponent(r.reviewer.stageName)}` : null;
            return (
              <li key={r.id} className="border-t pt-4 first:border-t-0 first:pt-0">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm font-semibold">
                    {href ? <Link href={href} className="hover:underline">{name}</Link> : name}
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={'h-3.5 w-3.5 ' + (n <= r.rating ? 'fill-current' : 'text-muted-foreground')}
                        strokeWidth={1.75}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {fmt(r.createdAt)}{r.updatedAt ? ' · edited' : ''}
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{r.body}</p>
              </li>
            );
          })}
        </ul>
      )}

      <WriteReviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        subjectPersonId={subjectPersonId}
        subjectName={subjectName}
        existing={existing}
        onSaved={refresh}
      />
    </section>
  );
}
